// Cache local SÍNCRONA de la resolución por feature. El router la lee en render (0ms)
// para decidir la variante sin esperar red; el hook la hidrata en background.
//
// Keyed por commerce_id (id_commerce de localStorage). resetStore en login NO borra
// localStorage, así que la preferencia sobrevive al re-login del mismo comercio.

import type {
  EvacReason,
  FeatureMenuState,
  FeatureVariant,
  FeatureVersionCacheEntry,
  InviteSurface,
  ManualEntry,
} from './types';

const CACHE_PREFIX = 'plick:feature-version:v1:';

export function getActiveCommerceId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('id_commerce');
  } catch {
    return null;
  }
}

function cacheKey(commerceId: string, featureKey: string): string {
  return `${CACHE_PREFIX}${commerceId}:${featureKey}`;
}

function normalizeVariant(value: unknown): FeatureVariant {
  return value === 'treatment' ? 'treatment' : 'control';
}

function normalizeMenuState(value: unknown): FeatureMenuState | null {
  if (value === 'exploring' || value === 'adopted' || value === 'reverted_sticky' || value === 'never_tried') {
    return value;
  }
  return null;
}

// Default conservador 'none': si el backend (cache viejo o degradado) no manda
// invite_surface, NO mostramos modal ni card intrusivos. El canal PULL (chip) ya no
// depende de invite_surface, sino de manual_entry.
function normalizeInviteSurface(value: unknown): InviteSurface {
  if (value === 'modal' || value === 'card' || value === 'none') {
    return value;
  }
  return 'none';
}

// Default conservador 'hidden': si el backend (cache viejo) no manda manual_entry, ocultamos
// el chip. El server siempre emite 'available'/'hidden'; este fallback solo cubre cache stale
// previo a la migración del contrato.
function normalizeManualEntry(value: unknown): ManualEntry {
  return value === 'available' ? 'available' : 'hidden';
}

// Default robusto 'iterating' (pausa temporal): si el backend no manda evac_reason, el aviso
// de ramp-down usa el mensaje tranquilizador en vez del de descontinuado. 'discontinued' solo
// se aplica cuando el backend lo emite explícitamente.
function normalizeEvacReason(value: unknown): EvacReason {
  return value === 'discontinued' ? 'discontinued' : 'iterating';
}

// Lee la entrada cacheada. Devuelve null si no hay commerce activo o no hay cache.
// Nunca lanza: degradación segura → el caller decide el fallback (control).
export function readCache(featureKey: string, commerceId = getActiveCommerceId()): FeatureVersionCacheEntry | null {
  if (typeof window === 'undefined' || !commerceId) return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(commerceId, featureKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FeatureVersionCacheEntry>;
    return {
      feature_key: featureKey,
      variant: normalizeVariant(parsed.variant),
      can_revert: Boolean(parsed.can_revert),
      ga_forced: Boolean(parsed.ga_forced),
      menu_state: normalizeMenuState(parsed.menu_state),
      experiment_id: typeof parsed.experiment_id === 'string' ? parsed.experiment_id : null,
      invite_surface: normalizeInviteSurface(parsed.invite_surface),
      manual_entry: normalizeManualEntry(parsed.manual_entry),
      next_eligible_at: typeof parsed.next_eligible_at === 'string' ? parsed.next_eligible_at : null,
      operator_evacuated: Boolean(parsed.operator_evacuated),
      evac_reason: normalizeEvacReason(parsed.evac_reason),
      compare_active: Boolean(parsed.compare_active),
      updated_at: typeof parsed.updated_at === 'number' ? parsed.updated_at : 0,
    };
  } catch {
    return null;
  }
}

export function writeCache(entry: FeatureVersionCacheEntry, commerceId = getActiveCommerceId()): void {
  if (typeof window === 'undefined' || !commerceId) return;
  try {
    window.localStorage.setItem(cacheKey(commerceId, entry.feature_key), JSON.stringify(entry));
  } catch {
    // ignore quota / private-mode
  }
}
